import { useRef, useState } from 'react';
import { forwardRef, useImperativeHandle } from 'react';
import { FloppyFill as IconSave, XCircleFill as IconTrash } from 'react-bootstrap-icons';
import './GridProfile.scss';
import InputFieldSet from '../../../repository/inputFieldSet/InputFieldSet';
import ModalDeleteCancel from '../../modals/deleteCancel/ModalDeleteCancel';
import useMain from '../../../common/MainContext';
import { languageElements } from './GridProfile-languageElements';
import LanguageElementsHandler from '../../../repository/languageElementsHandler/LanguageElementsHandler';
import FiveSecondsAlert from '../../alerts/fiveSecondsAlert/FiveSecondsAlert';

const GridProfile = forwardRef(function GridProfile(props, ref) {
    const { language, staticsGet, staticsSet, getLoginSettings, updateLoginSettings } = useMain();
    const refColumnStateSelector = useRef();
    const [modalDelete, setModalDelete] = useState({
        show: false,
        id: undefined,
    });
    const [stateOfFiveSecondsAlert, setStateOfFiveSecondsAlert] = useState({
        show: false,
        message: {
            title: '',
            body: '',
        }
    });

    useImperativeHandle(ref, () => {
        return {
            getGridColumns() {
                return getLayoutGridColumns();
            }
        }
    }, []);

    function currentProfileGet() {
        return staticsGet(['grids', props.loginSettingsKey, 'profileName']) || 'normal'
    }

    function currentProfileSet(newProfile) {
        if (props.loginSettingsKey) {
            staticsSet(['grids', props.loginSettingsKey, 'profileName'], newProfile || 'normal')
        }
    }

    function deleteGridLayout() {
        updateLoginSettings(
            ['gridProfiles', props.loginSettingsKey, currentProfileGet()],
            undefined,
            () => {
                props.setDataLoadingState('LOADING')
            });
    }

    function getLayoutGridColumns() {
        const currentLayout = getSavedLayout(currentProfileGet());
        const selectedFields = (currentLayout) ? currentLayout.map(column => column.colId) : props.report.reportParams.selectedColumns;
        const layoutGridColumns = selectedFields.map(columnName => {
            const columnDef = props.report.reportParams.columns.find(column => column.field === columnName);

            if (!columnDef) {
                return undefined;
            }

            const layoutDef = (currentLayout) ? currentLayout.find(column => column.colId === columnName) : undefined;

            return {
                field: columnName,
                type: columnDef.type,
                width: (layoutDef) ? layoutDef.width : columnDef.width,
                options: columnDef.options?.map(option => ({
                    value: option.value,
                    text: (option.textForGrid) ? option.textForGrid : option.text,
                })),
                pinned: (layoutDef) ? layoutDef.pinned : columnDef.pinned,
                hideFloatingFilter: columnDef.hideFloatingFilter,
                cellStyle: columnDef.cellStyle,
                cellRenderer: columnDef.cellRenderer,
                cellRendererParams: columnDef.cellRendererParams,
                sort: (layoutDef) ? layoutDef.sort : columnDef.sort,
                sortIndex: (layoutDef) ? layoutDef.sortIndex : columnDef.sortIndex,
                hide: (layoutDef) ? layoutDef.hide : columnDef.hide,
            };
        })

        return layoutGridColumns;
    }

    function getSavedLayout() {
        if (!props.loginSettingsKey) {
            return undefined;
        }
        return getLoginSettings(['gridProfiles', props.loginSettingsKey, currentProfileGet() || 'normal']);
    }

    function saveGridLayout() {
        const newLayout = (props.gridApi.getColumnState()).map(c => ({
            colId: c.colId,
            width: c.width,
            hide: c.hide,
            pinned: c.pinned,
            sort: c.sort,
            sortIndex: c.sortIndex,
        }));
        updateLoginSettings(
            ['gridProfiles', props.loginSettingsKey, currentProfileGet()],
            newLayout,
            () => {
            setStateOfFiveSecondsAlert({
                show: true,
                message: {
                    title: languageElementsHandler.get(`alert-saved-title`),
                },
                interval: 1500,
                className: 'text-center'
            })
        })
    };

    function handleColumnStateSelector(e) {
        if (currentProfileGet() === e.target.value) {
            return;
        }
        currentProfileSet(e.target.value);
        props.setDataLoadingState('LOADING')
    }

    const languageElementsHandler = new LanguageElementsHandler(
        languageElements,
        language,
    )

    if (!props.loginSettingsKey || props.visible === false) {
        return <></>
    }

    return (
        <div className="grid-profile">
            <FiveSecondsAlert
                show={stateOfFiveSecondsAlert.show}
                language={language}
                message={stateOfFiveSecondsAlert.message}
                variant={stateOfFiveSecondsAlert.variant}
                interval={stateOfFiveSecondsAlert.interval}
                className={stateOfFiveSecondsAlert.className}
                callback={() => { setStateOfFiveSecondsAlert({ show: false }); }}
            />

            {
                modalDelete.show &&
                <ModalDeleteCancel
                    show={modalDelete.show}
                    id={modalDelete.id}
                    language={language}
                    title={languageElementsHandler.get(`modal-delete-title`)}
                    body={languageElementsHandler.get(`modal-delete-body`)}
                    callback={(buttonId) => {
                        if (buttonId === 'DELETE') {
                            deleteGridLayout();
                        }
                        setModalDelete({ show: false });
                    }}
                />
            }

            <div className="grid-profile-left-pane">
                <InputFieldSet
                    reference={refColumnStateSelector}
                    className="field-columnStateSelector"
                    name="columnStateSelector"
                    type="select"
                    fieldValues={{
                        columnStateSelector: currentProfileGet(),
                    }}
                    labelText='Grid layout:'
                    optionList={[
                        { value: "", text: "normal" },
                        { value: "profile01", text: "profile01" },
                        { value: "profile02", text: "profile02" },
                    ]}
                    handleInputBlur={() => { }}
                    handleInputChange={handleColumnStateSelector}
                    required={true}
                />
                <div className="icons-area">
                    <IconSave className='columnStateSelector-icon normal' onClick={saveGridLayout} />
                    <IconTrash className='columnStateSelector-icon danger' onClick={() => {
                        setModalDelete({
                            show: true,
                            id: currentProfileGet(),
                        })
                    }} />
                </div>
            </div>
            <div className="ag-grid-container-footer-right-pane">
            </div>
        </div>
    )
});

export default GridProfile;
